import React, { useState, useEffect } from 'react';
import { X, Download, Smartphone } from 'lucide-react';
import { Button } from './ui/button';

/**
 * PWA Install Prompt - Shows install banner for PWA
 */
const PWAInstallPrompt = () => {
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [showBanner, setShowBanner] = useState(false);
    const [isIOS, setIsIOS] = useState(false);

    useEffect(() => {
        // Check if already installed
        if (window.matchMedia('(display-mode: standalone)').matches) {
            return;
        }

        // Check if dismissed recently
        const dismissed = localStorage.getItem('pwa-install-dismissed');
        if (dismissed) {
            const dismissedTime = parseInt(dismissed, 10);
            const dayInMs = 24 * 60 * 60 * 1000;
            if (Date.now() - dismissedTime < dayInMs * 7) {
                return; // Don't show for 7 days after dismissal
            }
        }

        // Check for iOS
        const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
        setIsIOS(isIOSDevice);

        // For iOS, show custom instructions
        if (isIOSDevice) {
            // Show after 30 seconds on iOS
            const timer = setTimeout(() => setShowBanner(true), 30000);
            return () => clearTimeout(timer);
        }

        // For other browsers, listen for beforeinstallprompt
        const handleBeforeInstall = (e) => {
            e.preventDefault();
            setDeferredPrompt(e);
            // Show after 15 seconds
            setTimeout(() => setShowBanner(true), 15000);
        };

        window.addEventListener('beforeinstallprompt', handleBeforeInstall);

        return () => {
            window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
        };
    }, []);

    const handleInstall = async () => {
        if (!deferredPrompt) return;

        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        
        // PWA installation handled
        if (outcome === 'accepted') {
            // Successfully installed
        }
        
        setDeferredPrompt(null);
        setShowBanner(false);
    };

    const handleDismiss = () => {
        setShowBanner(false);
        localStorage.setItem('pwa-install-dismissed', Date.now().toString());
    };

    if (!showBanner) return null;

    return (
        <div className="fixed bottom-20 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50 animate-fade-in">
            <div className="bg-white rounded-xl shadow-2xl border-2 border-amber-200 p-4 relative">
                {/* Close button */}
                <button 
                    onClick={handleDismiss}
                    className="absolute top-2 right-2 p-1 text-stone-400 hover:text-stone-600 transition-colors"
                    aria-label="Затвори"
                >
                    <X className="h-5 w-5" />
                </button>

                <div className="flex items-start gap-4">
                    {/* Icon */}
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#6B1028] to-[#8B1538] flex items-center justify-center flex-shrink-0">
                        <Smartphone className="h-7 w-7 text-amber-400" />
                    </div>

                    <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-[#6B1028] mb-1">
                            Инсталирай приложението
                        </h3>
                        <p className="text-sm text-[#6B1028]/70 mb-3">
                            {isIOS 
                                ? 'Натисни "Сподели" и след това "Добави към начален екран"'
                                : 'Добави на началния екран за бърз достъп!'
                            }
                        </p>

                        {!isIOS && deferredPrompt && (
                            <Button 
                                onClick={handleInstall}
                                className="btn-gold text-[#6B1028] text-sm py-2"
                                data-testid="pwa-install-btn"
                            >
                                <Download className="h-4 w-4 mr-2" />
                                Инсталирай
                            </Button>
                        )}

                        {isIOS && (
                            <div className="flex items-center gap-2 text-sm text-amber-600">
                                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M12 2L12 14M12 14L8 10M12 14L16 10M4 18L4 20L20 20L20 18" 
                                        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                                </svg>
                                <span>Сподели → Добави към начален екран</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PWAInstallPrompt;
